<script setup>
/**
 * My details (XN-4561, XN-4678, XN-4680): what a policyholder can change about
 * themselves, what the agency holds about them, their sign-up answers, and how
 * they sign in. It replaces the Gen 1 profile inside the portal; the roles that
 * still use /profile keep that. Prototype `profile()`, build 30 Sep 12:40.
 *
 * The page owns one copy of the details. Every read and every save answers
 * with the whole of it, and `apply` puts that answer in place — the pending
 * email change included — so no card holds a version of its own.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlModal from '../xl/XlModal.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import PortalDetailsEmailPending from './PortalDetailsEmailPending.vue';
import PortalDetailsForm from './PortalDetailsForm.vue';
import PortalDetailsPicture from './PortalDetailsPicture.vue';
import PortalDetailsSetBy from './PortalDetailsSetBy.vue';
import PortalDetailsSignUp from './PortalDetailsSignUp.vue';
import PortalDetailsSigningIn from './PortalDetailsSigningIn.vue';
import { myDetails } from '../../api/portal';
import { reportError } from '../../faro';
import { whoToAsk } from '../../helpers/coverage';
import { useAuthStore } from '../../stores/auth';

const UNREADABLE = 'Your details could not be read just now.';
// Roughly the five cards' heights, so the page does not jump when they arrive.
const SKELETON = ['124px', '360px', '250px', '150px', '130px'];

const auth = useAuthStore();

const state = ref('loading'); // loading | ready | error | refused
const details = ref(null);
const problem = ref('');

const hub = computed(() => details.value?.set_by?.hub?.name || 'your agency');
const ask = computed(() => whoToAsk(details.value?.set_by?.broker ?? null, hub.value));
// "message Maya" / "message your broker" / "message Northstar Insurance".
const messageWho = computed(() => (ask.value.has ? (ask.value.first ?? 'your broker') : hub.value));
// "ask Maya Lewis" / "ask your broker" / "ask Northstar Insurance".
const askWho = computed(() => (ask.value.has ? ask.value.name : hub.value));

// The portal header draws the session's copy of the account (auth.userData),
// so a new photo, name or colour is merged into it here and shows there at
// once — the merge Gen 1's Profile/Edit.vue made after its save, plus the
// colour. An empty photo is '', which XlAvatar reads as "no photo".
// The email goes too: chat and the session room read it from the same copy,
// which is loaded once per tab, and this page reads SSO fresh — so a change
// confirmed while signed in reaches them as soon as the page has read it.
function showInHeader(me) {
  if (!auth.userData) return;
  auth.setUserData({
    ...auth.userData,
    first_name: me.first_name,
    last_name: me.last_name,
    email: me.email,
    profile_image: me.photo || '',
    avatar_color: me.avatar_color ?? null,
  });
}

// Every read and every save goes through here: the page's one copy, and the header's.
function apply(fresh) {
  details.value = fresh;
  showInHeader(fresh.me);
}

const withPhoto = (url) => apply({ ...details.value, me: { ...details.value.me, photo: url } });

// Cancel takes the pending line away, and the button pressed with it: the
// focus goes to the field the change was about (the form's inputs have their
// names as ids). Nothing is re-read — the address they sign in with never moved.
async function cancelled() {
  apply({ ...details.value, email_change: null });
  await nextTick();
  document.getElementById('email')?.focus();
}

// A read that lands after the person left must not touch the session's header.
let gone = false;
onBeforeUnmount(() => { gone = true; });

async function load() {
  state.value = 'loading';
  try {
    const fresh = await myDetails();
    if (gone) return;
    apply(fresh);
    state.value = 'ready';
  } catch (error) {
    // Staff previewing the portal are refused /my/* by design.
    if (error?.response?.status === 403) {
      state.value = 'refused';
      return;
    }
    reportError(error);
    problem.value = error?.response?.data?.message || UNREADABLE;
    state.value = 'error';
  }
}

// XN-4688: unsaved edits are not lost silently. In-app navigation waits on the
// dialog (Esc and the backdrop mean Keep editing); a reload or closed tab gets
// the browser's own prompt.
const formRef = ref(null);
const settleLeave = ref(null); // set while the dialog is open: resolves the held navigation
let leaving = false; // Leave was chosen: a redirect re-running this guard must not ask again
const answer = (leave) => {
  if (leave) leaving = true;
  settleLeave.value?.(leave);
  settleLeave.value = null;
};
onBeforeRouteLeave(() => {
  if (leaving || !formRef.value?.unsaved) return true;
  answer(false);
  return new Promise((resolve) => { settleLeave.value = resolve; });
});

const warnOnUnload = (event) => {
  if (!formRef.value?.unsaved) return;
  event.preventDefault();
  event.returnValue = true;
};
onMounted(() => window.addEventListener('beforeunload', warnOnUnload));
onBeforeUnmount(() => window.removeEventListener('beforeunload', warnOnUnload));

onMounted(load);
</script>

<template>
  <section class="pdet">
    <h1 class="pdet__title">My details</h1>
    <p class="pdet__lede">Keep these up to date so we can reach you.</p>

    <div v-if="state === 'loading'" class="pdet__loading" aria-busy="true" data-testid="details-loading">
      <XlSkeleton v-for="height in SKELETON" :key="height" :height="height" radius="var(--xl-radius-lg)" />
    </div>

    <div v-else-if="state === 'error'" class="pdet__error" role="alert" data-testid="details-error">
      <h2>Your details couldn't be loaded</h2>
      <p>{{ problem }}</p>
      <XlButton variant="ghost" size="sm" @click="load()">Try again</XlButton>
    </div>

    <p v-else-if="state === 'refused'" class="pdet__calm" data-testid="details-refused">My details shows a policyholder their own details.</p>

    <template v-else>
      <PortalDetailsPicture :me="details.me" :hub="hub" :has-broker="ask.has" @saved="apply" @photo="withPhoto" />
      <PortalDetailsForm ref="formRef" :me="details.me" :ask-who="askWho" @saved="apply">
        <template #email-note>
          <PortalDetailsEmailPending
            v-if="details.email_change"
            :change="details.email_change"
            :current="details.me.email"
            @saved="apply"
            @cancelled="cancelled"
          />
        </template>
      </PortalDetailsForm>
      <PortalDetailsSetBy :set-by="details.set_by" :hub="hub" :message-who="messageWho" />
      <PortalDetailsSignUp v-if="details.sign_up?.length" :answers="details.sign_up" :message-who="messageWho" />
      <PortalDetailsSigningIn :email="details.me.email" />
    </template>

    <XlModal v-if="settleLeave" title="Leave without saving?" max-width="440px" @close="answer(false)">
      <p class="pdet__leave">Your changes to your details haven't been saved.</p>
      <template #actions>
        <XlButton variant="ghost" autofocus @click="answer(false)">Keep editing</XlButton>
        <XlButton variant="danger" @click="answer(true)">Leave without saving</XlButton>
      </template>
    </XlModal>
  </section>
</template>

<style scoped>
/* Measurements from the prototype's profile() header, build 30 Sep 12:40. */
.pdet__title { font-size: 32px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.pdet__lede { font-size: 16.5px; color: var(--xl-color-on-surface-variant); margin: 0 0 24px; }
.pdet__loading { display: grid; gap: 14px; }
.pdet__leave { margin: 0; color: var(--xl-color-on-surface-variant); }
.pdet__calm { margin: 0; color: var(--xl-color-on-surface-variant); }
.pdet__error {
  padding: 18px 20px;
  border-radius: 16px;
  font-size: 14.5px;
  background: var(--xl-color-error-container, #FBEAEA);
  border: 1px solid var(--xl-color-error);
  color: var(--xl-color-error);
}
.pdet__error h2 { margin: 0 0 4px; font-size: 16px; font-weight: 600; }
.pdet__error p { margin: 0 0 12px; }
@media (max-width: 480px) { .pdet__title { font-size: 28px; } }
</style>
